// SharedCard · Iter108
// Wrapper minimale che centralizza la logica della "banda colorata" di stato
// applicata alle card di Preventivi, Manutenzioni, Sinistri e Credius.
//
// Regole di priorità (invariata rispetto all'implementazione precedente):
//  - Gli stati DEFINITIVI (Ricevuto/Sottoscritto/Chiusa/Eseguito/Conclusa/Definito...)
//    prevalgono sempre sui flag temporali (in_ritardo, in_scadenza).
//  - I flag temporali si applicano solo se lo stato non è definitivo.
//
// Ogni modulo continua a gestire il proprio contenuto interno: qui centralizziamo
// solo il calcolo della classe di bordo sinistro.

import { Card } from "./ui/card";

// --- Preventivi ---------------------------------------------------------
export function getBandPreventivo(p) {
  const sk = p?.stato_key || p?.stato;
  if (sk === "Sottoscritto") return "border-l-8 border-l-green-900";
  if (sk === "IncaricoAccettato") return "border-l-8 border-l-green-500";
  if (sk === "Ricevuto") return "border-l-8 border-l-green-700";
  if (sk === "Rifiutato") return "border-l-8 border-l-slate-400";
  if (p?.in_ritardo) return "border-l-8 border-l-red-600";
  if (p?.in_scadenza) return "border-l-8 border-l-amber-500";
  if (p?.archiviato) return "border-l-8 border-l-slate-300";
  return "";
}

// --- Manutenzioni (Tickets) --------------------------------------------
export function getBandTicket(t) {
  if (t?.stato === "Chiusa") return "border-l-8 border-l-green-900";
  if (t?.stato === "Eseguito") return "border-l-8 border-l-green-600";
  if (t?.stato === "Annullata") return "border-l-8 border-l-slate-400";
  // Iter122cp · "In lavorazione" ora giallo fluo · se in ritardo → arancione fluo
  if (t?.stato === "In lavorazione") {
    return t?.in_ritardo
      ? "border-l-8 border-l-orange-400 shadow-[inset_6px_0_10px_-4px_rgba(251,146,60,0.55)]"
      : "border-l-8 border-l-yellow-400 shadow-[inset_6px_0_10px_-4px_rgba(250,204,21,0.55)]";
  }
  if (t?.stato === "Affidata") return "border-l-8 border-l-indigo-500";
  if (t?.in_ritardo) return "border-l-8 border-l-red-600";
  if (t?.in_scadenza) return "border-l-8 border-l-amber-500";
  return "";
}

// --- Sinistri ----------------------------------------------------------
// Iter122fp18 · Banda laterale per fase (mappa granulare come richiesto dall'utente):
//   Bozza: grigio · Istruttoria: giallo fluo · Valutazione: arancio fluo
//   Liquidazione: rossa · Chiuso: verde
const BORDO_FASE_SINISTRO = {
  "Bozza-Denuncia": "border-l-8 border-l-slate-400",
  "Aperto-Istruttoria": "border-l-8 border-l-yellow-400",
  "Aperto-Valutazione": "border-l-8 border-l-orange-500",
  "Aperto-Liquidazione": "border-l-8 border-l-red-600",
  "Chiuso-Liquidato": "border-l-8 border-l-emerald-600",
};
export function getBandSinistro(s) {
  const fase = s?.fase;
  if (fase && BORDO_FASE_SINISTRO[fase]) return BORDO_FASE_SINISTRO[fase];
  // Fallback per record legacy con fasi vecchie (Aperto/In gestione/Definito)
  if (fase === "Definito") return "border-l-8 border-l-emerald-600";
  if (fase === "In gestione") return "border-l-8 border-l-orange-500";
  if (fase === "Aperto") return "border-l-8 border-l-yellow-400";
  return "border-l-8 border-l-slate-400";
}

// --- Credius (pratiche legali) -----------------------------------------
const BORDO_STATO_CREDIUS = {
  "Conclusa": "border-l-8 border-l-green-900",
  "In gestione": "border-l-8 border-l-green-500",
  "Affidata": "border-l-8 border-l-sky-500",
  "Preventivo": "border-l-8 border-l-indigo-500",
  "Da affidare": "border-l-8 border-l-amber-500",
};
export function getBandCredius(p) {
  if (p?.incarico_stato === "Rifiutato" || p?.revocata_at) {
    return "border-l-8 border-l-slate-400";
  }
  return BORDO_STATO_CREDIUS[p?.stato] || "";
}

// Iter122k33 · Badge di stato Credius (pill) allineato alla banda `getBandCredius`.
// Esportato per riuso nelle liste (pagina Credius + dashboard).
export const CREDIUS_STATO_PILL = {
  "Da affidare":  "bg-amber-100 text-amber-800 border-amber-400",
  "Preventivo":   "bg-indigo-100 text-indigo-800 border-indigo-400",
  "Affidata":     "bg-sky-100 text-sky-800 border-sky-400",
  "In gestione":  "bg-emerald-100 text-emerald-800 border-emerald-400",
  "Conclusa":     "bg-green-900 text-white border-green-900",
};
export function getPillCredius(stato) {
  return CREDIUS_STATO_PILL[stato] || "bg-slate-100 text-slate-700 border-slate-300";
}

// --- Wrapper generico ---------------------------------------------------
// Uso: <SharedCard band={bordo} className="..." {...rest}>...children...</SharedCard>
// Renderizza un `Card` shadcn identico a prima, semplicemente unendo `band` alla className.
// Iter109 · aggiunta micro-animazione: se è presente una banda colorata, l'hover la espande
// leggermente (da border-l-8 a border-l-[12px]) con transizione fluida.
export function SharedCard({ band = "", className = "", children, ...rest }) {
  const hasBand = band.includes("border-l-");
  const hoverBoost = hasBand ? "hover:border-l-[12px] transition-[border-left-width,border-color] duration-150" : "";
  const merged = `${className} ${band} ${hoverBoost}`.trim();
  return (
    <Card className={merged} {...rest}>
      {children}
    </Card>
  );
}
